import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  IconButton,
  Typography,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Button } from "react-bootstrap";
import DataGridTable from "components/Table/DataGrid";
import { useParams } from "react-router-dom";
import get from "lodash/get";
import NoResultsFound from "components/NoResults/NoResultsFound";
import AppRoleContext from "context/AppRoleContext";
import HiddenOn from "components/Wrappers/HiddenOn";
import { hasAppRole, isJobManager, roleConfigKeys } from "core/utils/roleUtils";
import { warrantyClearShowSnackbar } from "actions/warrantyActions";
import {
  SETTINGS_DELETE_MARKET_SECTOR_SUCCESS,
  SETTINGS_MARKET_SECTOR_ERROR,
} from "constants/types";

import { getMarketSector } from "selectors/marketSectorSelectors";
import AddNewMarketSectors from "./AddNewMarketSectors";
import {
  fetchMarketSector,
  marketSectorDelete,
} from "actions/marketSectorsActions";
import { marketSectorListColumns } from "./MarketSectorsUtils";

export const MarketSectors = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const {
    marketSector,
    totalCount,
    loading,
    showSnackbar,
    status,
    statusText,
  } = useSelector(getMarketSector);

  const [addNewJob, setAddNewJob] = useState(false);

  const [pageModel, setPageModel] = React.useState({
    page: 0,
    pageSize: 10,
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [editJob, setEditJob] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [userManagement, setUserManagement] = useState(false);
  const [jobActionsContext, setJobActionsContext] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [makeDelete, setMakeDelete] = useState(false);

  const appRoleDetails = useContext(AppRoleContext);

  const promptDelete = () => {
    setShowConfirmModal(true);
  };

  useEffect(() => {
    setShowConfirmModal(false);
    if (makeDelete) {
      bulkDelete(selectedRows);
      setMakeDelete(false);
    }
  }, [makeDelete]);

  useEffect(() => {
    dispatch(
      fetchMarketSector({
        ...pageModel,
      })
    );
  }, [dispatch, pageModel]);

  useEffect(() => {
    console.log("selected", selectedJob);
  }, [selectedJob]);

  const bulkDelete = async (selectedFiles) => {
    try {
      const results = await Promise.allSettled(
        selectedFiles.map((fileId) =>
          dispatch(
            marketSectorDelete({
              marketSectorId: fileId,
              showSnackbar: false,
            })
          )
        )
      );

      const failedDeletions = results.filter(
        (result) => result.status === "rejected"
      );
      setSelectedJob(null);

      if (failedDeletions.length > 0) {
        console.error(
          "Failed to delete the following marketSector:",
          failedDeletions
        );
        dispatch({
          type: SETTINGS_MARKET_SECTOR_ERROR,
          payload: {
            response: null,
            status: "error",
            message: "Some marketSector failed to delete",
            loading: false,
            showSnackbar: true,
          },
        });
      } else {
        dispatch({
          type: SETTINGS_DELETE_MARKET_SECTOR_SUCCESS,
          payload: {
            response: null,
            status: "success",
            message: "marketSector Deleted Successfully",
            loading: false,
            showSnackbar: true,
          },
        });
      }

      await dispatch(
        fetchMarketSector({
          ...pageModel,
        })
      );
    } catch (error) {
      console.error("Error during bulk delete:", error);
      showSnackbar("error", "Error deleting files");
    }
  };

  return (
    <div className="jobs-page">
      <Dialog
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        fullWidth={true}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure to delete selected record ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmModal(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={() => setMakeDelete(true)} variant="danger">
            Sure
          </Button>
        </DialogActions>
      </Dialog>
      {addNewJob ? (
        <AddNewMarketSectors
          onCancel={() => setAddNewJob(false)}
          selectedJob={selectedJob}
          title={get(selectedJob, "data.marketSector", null)}
          params={params}
          onSave={() => {
            dispatch(
              fetchMarketSector({
                ...pageModel,
              })
            );
          }}
        />
      ) : (
        <>
          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={showSnackbar}
            autoHideDuration={6000}
            onClose={() => warrantyClearShowSnackbar()}
          >
            <Alert
              onClose={() => warrantyClearShowSnackbar()}
              severity={status}
            >
              {statusText}
            </Alert>
          </Snackbar>

          <div className="projects-header-section d-flex justify-content-between mt-3">
            <div>
              <Typography className="h1">MarketSectors</Typography>
              {/* <BreadCrumb /> */}
            </div>
            <HiddenOn
              isHidden={!hasAppRole(roleConfigKeys.CREATE_JOB, appRoleDetails)}
            >
              {!addNewJob && (
                <div className="d-flex justify-content-end ">
                  <button
                    className="primaryButton addProjectButton"
                    style={{ width: "8rem" }}
                    onClick={() => {
                      setSelectedJob(null);
                      setAddNewJob(!addNewJob);
                    }}
                  >
                    {"+ Add New Item"}
                  </button>
                </div>
              )}
            </HiddenOn>
          </div>
          <div className="projects-content-section">
            {totalCount > 1 ? (
              <DataGridTable
                pagination={false}
                totalCount={totalCount}
                isLoading={loading}
                disableBulkDelete={true}
                columns={marketSectorListColumns({
                  setEditJob,
                  setAddNewJob,
                  setSelectedJob,
                  appRoleDetails,
                  onCompanyDelete: (marketSectorId) => {
                    setSelectedRows([marketSectorId]);
                    promptDelete();
                  },
                  setUserManagement,
                  setJobActionsContext,
                })}
                data={marketSector}
                defineRowId={(row) => {
                  return row.marketSectorId;
                }}
                paginationModel={pageModel}
                onPaginationChange={(props) => {
                  setPageModel(props);
                }}
                onSelect={(props) => {
                  setSelectedRows(props);
                }}
                selectedRows={selectedRows}
                onDeleteSelection={() => {
                  if (selectedRows.length > 0) {
                    promptDelete();
                  }
                }}
                onRowClick={(row) => {
                  setAddNewJob(!addNewJob);
                  setSelectedJob({ data: row.row });
                  //navigate(`/settings/companies/view/${row.id}`);
                }}
              />
            ) : (
              <NoResultsFound />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MarketSectors;
